import React, { useState, useEffect, useId } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { EditorProvider, useCurrentEditor } from '@tiptap/react';
import { generateJSON } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { COLORS } from '../../constants/radar';
import { decodeHtml } from '../MyActivities/MyCV/helpers/ProcessTemplate';

// Reusable button component for the editor
const EditorButton = ({
  onClick,
  disabled = false,
  isActive = false,
  children
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 16px',
        backgroundColor: isActive ? COLORS.PRIMARY : '#f0f0f0',
        color: isActive ? 'white' : '#333',
        border: 'none',
        borderRadius: '4px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 500,
        fontSize: '14px',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
};

// MenuBar component for full variant
const MenuBar = ({ description, readonly = false }) => {
  const { editor } = useCurrentEditor();
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (editor) {
      // Update character count when content changes
      const updateCharCount = () => {
        setCharCount(editor.getText().length);
      };

      editor.on('update', updateCharCount);

      // Initial count
      updateCharCount();

      return () => {
        editor.off('update', updateCharCount);
      };
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <>
      {description && (
        <Typography variant="body1" sx={{ color: '#6e7c91', mb: 3 }}>
          {description}
        </Typography>
      )}

      <Box className="button-group" sx={{
        display: 'flex',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1
      }}>
        <EditorButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={true}
          isActive={editor.isActive('bold')}
        >
          Bold
        </EditorButton>

        <EditorButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={!editor.can().chain().focus().toggleBulletList().run() || readonly}
          isActive={editor.isActive('bulletList')}
        >
          Bullet list
        </EditorButton>

        <EditorButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={!editor.can().chain().focus().toggleOrderedList().run() || readonly}
          isActive={editor.isActive('orderedList')}
        >
          Ordered list
        </EditorButton>

        <div style={{
          borderLeft: '1px solid #d1d5db',
          height: '24px',
          margin: '0 8px'
        }}></div>

        <EditorButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run() || readonly}
        >
          Undo
        </EditorButton>

        <EditorButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run() || readonly}
        >
          Redo
        </EditorButton>
      </Box>

      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        px: 1,
        mt: 2
      }}>
        <Typography variant="caption" sx={{ color: '#6e7c91' }}>
          {charCount} / 400+
        </Typography>
      </Box>
    </>
  );
};

// Compact toolbar button component
const CompactToolbarButton = ({ onClick, disabled, isActive, title, children }) => (
  <Tooltip title={title} arrow>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          color: isActive ? '#2563EB' : '#6B7280',
          backgroundColor: isActive ? '#EFF6FF' : 'transparent',
          borderRadius: '4px',
          padding: '4px',
          '&:hover': {
            backgroundColor: isActive ? '#DBEAFE' : '#F3F4F6',
          },
          '&.Mui-disabled': {
            color: '#D1D5DB',
          },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

// Compact MenuBar component for compact variant
const CompactMenuBar = ({ readonly = false }) => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderBottom: '1px solid #E5E7EB',
        px: 1,
        py: 0.5,
        backgroundColor: '#FAFAFA',
      }}
    >
      <CompactToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run() || readonly}
        isActive={editor.isActive('bold')}
        title="Bold"
      >
        <FormatBoldIcon sx={{ fontSize: 18 }} />
      </CompactToolbarButton>

      <CompactToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run() || readonly}
        isActive={editor.isActive('italic')}
        title="Italic"
      >
        <FormatItalicIcon sx={{ fontSize: 18 }} />
      </CompactToolbarButton>

      <Box sx={{ borderLeft: '1px solid #E5E7EB', height: 20, mx: 0.5 }} />

      <CompactToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={!editor.can().chain().focus().toggleBulletList().run() || readonly}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <FormatListBulletedIcon sx={{ fontSize: 18 }} />
      </CompactToolbarButton>

      <CompactToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={!editor.can().chain().focus().toggleOrderedList().run() || readonly}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <FormatListNumberedIcon sx={{ fontSize: 18 }} />
      </CompactToolbarButton>

      <Box sx={{ borderLeft: '1px solid #E5E7EB', height: 20, mx: 0.5 }} />

      <CompactToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run() || readonly}
        title="Undo"
      >
        <UndoIcon sx={{ fontSize: 18 }} />
      </CompactToolbarButton>

      <CompactToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run() || readonly}
        title="Redo"
      >
        <RedoIcon sx={{ fontSize: 18 }} />
      </CompactToolbarButton>
    </Box>
  );
};

// Add custom styles for the editor
const TiptapStyles = ({ variant = 'full', minHeight }) => {
  useEffect(() => {
    const styleId = `rich-text-editor-styles-${variant}`;

    // Check if styles already exist
    if (document.getElementById(styleId)) {
      return;
    }

    const style = document.createElement('style');
    style.id = styleId;

    if (variant === 'compact') {
      const height = minHeight || 80;
      style.innerHTML = `
        .compact-editor-container {
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          overflow: hidden;
          background-color: white;
        }

        .compact-editor-container:focus-within {
          border-color: #2563EB;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }

        .compact-editor-container .ProseMirror {
          min-height: ${height}px;
          max-height: 200px;
          overflow-y: auto;
          padding: 12px;
          font-size: 14px;
          line-height: 1.5;
        }

        .compact-editor-container .ProseMirror:focus {
          outline: none;
        }

        .compact-editor-container .ProseMirror p {
          margin: 0 0 8px 0;
        }

        .compact-editor-container .ProseMirror p:last-child {
          margin-bottom: 0;
        }

        .compact-editor-container .ProseMirror ul,
        .compact-editor-container .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 8px 0;
        }

        .compact-editor-container .ProseMirror li {
          margin: 4px 0;
        }
      `;
    } else {
      style.innerHTML = `
        .editor-container {
          overflow-y: auto;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          background-color: white;
        }

        .ProseMirror {
          min-height: 180px;
          padding: 16px;
          background-color: white;
          overflow-y: auto;
          font-size: 15px;
        }

        .ProseMirror:focus {
          outline: none;
        }

        .ProseMirror p {
          margin: 0;
        }

        /* List styles */
        .ProseMirror ul,
        .ProseMirror ol {
          padding: 0 1rem;
          margin: 0.5rem 0;
        }
      `;
    }
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, [variant, minHeight]);

  return null;
};

/**
 * RichTextEditor - A rich text editor with full and compact variants
 *
 * @param {Object} props
 * @param {string} props.initialContent - The initial HTML content (for full variant)
 * @param {string} props.value - The HTML content value (for compact variant, alternative to initialContent)
 * @param {string} props.description - Description text shown above toolbar (full variant only)
 * @param {string} props.footer - Footer text shown below editor (full variant only)
 * @param {Function} props.onChange - Callback when content changes: (html) => void
 * @param {boolean} props.readonly - Whether the editor is read-only
 * @param {boolean} props.disabled - Whether the editor is disabled (compact variant)
 * @param {'full' | 'compact'} props.variant - Editor variant (default: 'full')
 * @param {number} props.minHeight - Minimum height for compact variant (default: 80)
 * @param {boolean} props.showToolbar - Whether to show toolbar in compact variant (default: true)
 */
const RichTextEditor = ({
  initialContent = '',
  value,
  description,
  footer,
  onChange,
  readonly = false,
  disabled = false,
  variant = 'full',
  minHeight = 80,
  showToolbar = true,
}) => {
  const componentId = useId();
  const extensions = [
    StarterKit.configure({
      bulletList: {
        keepMarks: true,
        keepAttributes: false,
      },
      orderedList: {
        keepMarks: true,
        keepAttributes: false,
      },
    }),
    Underline,
  ];

  // Get content - support both initialContent (full) and value (compact) props
  const content = value !== undefined ? value : initialContent;

  // Parse content for the editor
  const getEditorContent = () => {
    if (!content) {
      return '';
    }
    // First decode any HTML entities (e.g., &lt; -> <)
    const decodedContent = decodeHtml(content);
    // If content looks like HTML, parse it
    if (decodedContent.includes('<') && decodedContent.includes('>')) {
      try {
        return generateJSON(decodedContent, extensions);
      } catch {
        return decodedContent;
      }
    }
    // Plain text - return as is, editor will handle it
    return decodedContent;
  };

  const isCompact = variant === 'compact';
  const isDisabled = readonly || disabled;

  // Render compact variant
  if (isCompact) {
    return (
      <Box sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
        <TiptapStyles variant="compact" minHeight={minHeight} />

        <EditorProvider
          key={`editor-${componentId}`}
          slotBefore={showToolbar ? <CompactMenuBar readonly={isDisabled} /> : null}
          extensions={extensions}
          content={getEditorContent()}
          editable={!isDisabled}
          editorContainerProps={{
            className: 'compact-editor-container',
          }}
          onUpdate={({ editor }) => {
            if (onChange) {
              const html = editor.getHTML();
              // Return empty string if content is just empty paragraph
              if (html === '<p></p>') {
                onChange('');
              } else {
                onChange(html);
              }
            }
          }}
        />
      </Box>
    );
  }

  // Render full variant
  return (
    <Box>
      <TiptapStyles variant="full" />

      <EditorProvider
        key={`editor-${componentId}`}
        slotBefore={<MenuBar description={description} readonly={readonly} />}
        extensions={extensions}
        content={getEditorContent()}
        editable={!readonly}
        parseOptions={{
          preserveWhitespace: 'full',
        }}
        editorProps={{
          attributes: {
            class: 'custom-editor-content',
          },
        }}
        editorContainerProps={{
          className: 'editor-container',
        }}
        onUpdate={({ editor }) => {
          if (onChange) {
            onChange(editor.getHTML());
          }
        }}
      >
        {footer && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: '#6e7c91' }}>
              {footer}
            </Typography>
          </Box>
        )}
      </EditorProvider>
    </Box>
  );
};

export default RichTextEditor;
